import * as RA from "fp-ts/ReadonlyArray";
import * as O from "fp-ts/Option";
import { pipe } from "fp-ts/function";
import * as Ord from "fp-ts/Ord";
import * as Str from "fp-ts/string";

import { Choice } from "src/components/select/select";
import { School, SchoolEdge } from "src/generated/types";

export const byValue: Ord.Ord<Choice> = Ord.fromCompare((c1, c2) =>
    Str.Ord.compare(c1.value.toLocaleLowerCase(), c2.value.toLocaleLowerCase()),
);

const reportYearOptions: ReadonlyArray<Choice> = [
    { id: "2019-2020", value: "2019-2020" },
    { id: "2020-2021", value: "2020-2021" },
    { id: "2021-2022", value: "2021-2022" },
    { id: "2022-2023", value: "2022-2023" },
    { id: "2023-2024", value: "2023-2024" },
    { id: "2024-2025", value: "2024-2025" },
    { id: "2025-2026", value: "2025-2026" },
    { id: "2026-2027", value: "2026-2027" },
];

const reportTypeOptions: ReadonlyArray<Choice> = [
    { id: "fsci", value: "FSCI" },
    { id: "ffi", value: "FFI" },
    { id: "fsci-ffi", value: "FSCI & FFI" },
];

export const getReportYears = (): ReadonlyArray<Choice> => {
    return pipe(reportYearOptions, RA.sort(byValue), RA.reverse, RA.prepend({ id: "*", value: "Select Year..." }));
};

export const getReportTypes = (): ReadonlyArray<Choice> => {
    return pipe(reportTypeOptions, RA.sort(byValue), RA.reverse, RA.prepend({ id: "*", value: "Select Type..." }));
};

/*
 * The label a school gets in an admin dropdown. The ACSI account number is appended when the school
 * has one so that same-named schools can be told apart (INC-25784); schools without one keep the
 * original "Name (Address)" label.
 */
export const formatSchoolChoice = (school: Pick<School, "accountNumber" | "address" | "name">): string => {
    const label = `${school.name} (${school.address})`;
    const accountNumber = school.accountNumber?.trim();

    return accountNumber ? `${label} — #${accountNumber}` : label;
};

export const getSchoolChoices = (schools: ReadonlyArray<SchoolEdge>): ReadonlyArray<Choice> => {
    return pipe(
        schools,
        RA.map((schoolEdge): Choice => {
            return pipe(
                O.fromNullable(schoolEdge.node),
                O.map((node) => ({
                    id: node.id,
                    value: formatSchoolChoice(node),
                })),
                O.getOrElse(() => ({ id: "", value: "" })),
            );
        }),
        RA.sort(byValue),
        RA.prepend({ id: "*", value: "Select School..." }),
    );
};
